import { useState, useCallback } from 'react'
import { api } from '../../utils/api'
import { useDcStore } from '../../stores/dcStore'
import {
  Search, Loader2, AlertTriangle, ChevronDown, ChevronRight,
  Server, Network, Cable, Layers, RefreshCw, CheckSquare, Square,
  MinusSquare, Filter, Users, CalendarRange
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Site {
  id: number
  name: string
  slug: string
  racks_count: number
  devices_count: number
}

interface PreviewSummary {
  racks: { create: number; update: number; skip: number; conflict: number; total: number }
  devices: { create: number; update: number; skip: number; conflict: number; total: number }
  ports: { create: number; update: number; skip: number; conflict: number; total: number }
  cables: { create: number; update: number; skip: number; conflict: number; total: number }
  tenants: { create: number; update: number; skip: number; conflict: number; total: number }
  reservations: { create: number; update: number; skip: number; conflict: number; total: number }
  warnings: number
  skipped_cables: number
}

interface RackOp {
  netbox_id: number
  name: string
  action: 'create' | 'update' | 'skip' | 'conflict'
  u_slots?: number
}

interface PreviewResult {
  summary: PreviewSummary
  racks: RackOp[]
  devices: any[]
  ports: any[]
  cables: any[]
  warnings: string[]
  skipped_cables: string[]
}

interface PreviewStepProps {
  url: string
  token: string
  sites: Site[]
  onPreviewReady: (
    preview: PreviewResult,
    dcCode: string,
    siteId: number | null,
    conflictStrategy: string,
    selectedRackNbIds: number[] | null
  ) => void
}

const ACTION_COLORS: Record<string, string> = {
  create: 'text-green-400 bg-green-400/10',
  update: 'text-yellow-400 bg-yellow-400/10',
  skip: 'text-text-muted bg-bg-panel',
  conflict: 'text-red-400 bg-red-400/10',
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Создать',
  update: 'Обновить',
  skip: 'Пропустить',
  conflict: 'Конфликт',
}

function SummaryCard({
  icon: Icon, label, counts
}: {
  icon: any, label: string, counts: PreviewSummary['racks']
}) {
  if (counts.total === 0) return null
  return (
    <div className="rounded-lg border border-border bg-bg-panel p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-accent-primary" />
        <span className="text-sm font-medium text-text-primary">{label}</span>
        <span className="ml-auto text-xs text-text-muted">{counts.total} объектов</span>
      </div>
      <div className="flex gap-1 flex-wrap">
        {counts.create > 0 && (
          <span className="px-2 py-0.5 rounded text-xs bg-green-400/10 text-green-400">
            +{counts.create} создать
          </span>
        )}
        {counts.update > 0 && (
          <span className="px-2 py-0.5 rounded text-xs bg-yellow-400/10 text-yellow-400">
            ~{counts.update} обновить
          </span>
        )}
        {counts.skip > 0 && (
          <span className="px-2 py-0.5 rounded text-xs bg-bg-input text-text-muted">
            {counts.skip} без изменений
          </span>
        )}
        {counts.conflict > 0 && (
          <span className="px-2 py-0.5 rounded text-xs bg-red-400/10 text-red-400">
            ⚠ {counts.conflict} конфликт
          </span>
        )}
      </div>
    </div>
  )
}

/** Selective rack picker — checkboxes for each rack in preview */
function RackSelector({
  racks,
  selected,
  onChange,
}: {
  racks: RackOp[]
  selected: Set<number>
  onChange: (s: Set<number>) => void
}) {
  const [open, setOpen] = useState(true)
  const actionable = racks.filter(r => r.action !== 'skip')
  const allSelected = actionable.every(r => selected.has(r.netbox_id))
  const noneSelected = actionable.every(r => !selected.has(r.netbox_id))

  const toggleAll = () => {
    if (allSelected) {
      onChange(new Set())
    } else {
      onChange(new Set(actionable.map(r => r.netbox_id)))
    }
  }

  const toggle = (id: number) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-bg-panel hover:bg-white/5 text-sm text-text-primary transition-all"
      >
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-accent-primary" />
          <span className="font-medium">Выбор стоек для импорта</span>
          <span className="text-xs text-text-secondary bg-bg-input px-1.5 py-0.5 rounded">
            {selected.size}/{actionable.length}
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-text-secondary" /> : <ChevronRight className="w-4 h-4 text-text-secondary" />}
      </button>

      {open && (
        <div className="border-t border-border">
          {/* Select all row */}
          <div className="flex items-center gap-2 px-4 py-2 bg-bg-base border-b border-border/50">
            <button onClick={toggleAll} className="text-text-secondary hover:text-accent-primary transition-colors">
              {allSelected ? (
                <CheckSquare className="w-4 h-4 text-accent-primary" />
              ) : noneSelected ? (
                <Square className="w-4 h-4" />
              ) : (
                <MinusSquare className="w-4 h-4 text-yellow-400" />
              )}
            </button>
            <span className="text-xs text-text-secondary font-medium">
              {allSelected ? 'Снять все' : 'Выбрать все'}
            </span>
            <span className="ml-auto text-xs text-text-muted">
              {selected.size === 0 ? (
                <span className="text-yellow-400">Импорт всех стоек</span>
              ) : (
                <span className="text-accent-primary">Избирательный импорт</span>
              )}
            </span>
          </div>

          {/* Rack list */}
          <div className="max-h-52 overflow-y-auto divide-y divide-border/30">
            {actionable.length === 0 && (
              <div className="px-4 py-3 text-xs text-text-muted">Нет стоек для импорта</div>
            )}
            {actionable.map(r => (
              <div
                key={r.netbox_id}
                className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-white/5 transition-all ${
                  selected.has(r.netbox_id) ? 'bg-accent-primary/5' : ''
                }`}
                onClick={() => toggle(r.netbox_id)}
              >
                <div className="text-text-secondary hover:text-accent-primary transition-colors shrink-0">
                  {selected.has(r.netbox_id) ? (
                    <CheckSquare className="w-4 h-4 text-accent-primary" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </div>
                <Server className="w-3.5 h-3.5 text-text-muted shrink-0" />
                <span className="text-xs text-text-primary font-mono flex-1 truncate">
                  {r.name || `NetBox #${r.netbox_id}`}
                </span>
                {r.u_slots && (
                  <span className="text-[10px] text-text-muted">{r.u_slots}U</span>
                )}
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${ACTION_COLORS[r.action] || ''}`}>
                  {ACTION_LABELS[r.action] || r.action}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function NetBoxPreviewStep({ url, token, sites, onPreviewReady }: PreviewStepProps) {
  const { datacenters } = useDcStore()
  const [selectedSiteId, setSelectedSiteId] = useState<number | ''>(
    sites.length === 1 ? sites[0].id : ''
  )
  const [selectedDc, setSelectedDc] = useState(datacenters[0]?.code || '')
  const [conflictStrategy, setConflictStrategy] = useState('netbox_wins')
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [error, setError] = useState('')

  // Selected rack NetBox IDs — empty Set means "import all"
  const [selectedRackIds, setSelectedRackIds] = useState<Set<number>>(new Set())

  const fetchPreview = useCallback(async () => {
    if (!selectedDc) { toast.error('Выберите ЦОД'); return }
    setLoading(true)
    setError('')
    setPreview(null)
    setSelectedRackIds(new Set()) // reset selection on new preview
    try {
      const res = await api.post('/api/integrations/netbox/preview', {
        url: url || undefined,
        token: token || undefined,
        site_id: selectedSiteId || null,
        dc_code: selectedDc,
        conflict_strategy: conflictStrategy,
        use_saved: true,
      })
      setPreview(res.data.preview)
      // Auto-select all actionable racks
      const actionable = (res.data.preview?.racks || []).filter((r: RackOp) => r.action !== 'skip')
      setSelectedRackIds(new Set(actionable.map((r: RackOp) => r.netbox_id)))
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message || 'Preview failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }, [url, token, selectedSiteId, selectedDc, conflictStrategy])

  const hasAnything = preview && (
    (preview.summary.racks.create        + preview.summary.racks.update)        > 0 ||
    (preview.summary.devices.create      + preview.summary.devices.update)      > 0 ||
    (preview.summary.ports.create        + preview.summary.ports.update)        > 0 ||
    (preview.summary.cables.create       + preview.summary.cables.update)       > 0 ||
    ((preview.summary.tenants?.create    ?? 0) + (preview.summary.tenants?.update    ?? 0)) > 0 ||
    ((preview.summary.reservations?.create ?? 0) + (preview.summary.reservations?.update ?? 0)) > 0
  )

  const handleProceed = () => {
    if (!preview) return
    const allActionable = preview.racks.filter(r => r.action !== 'skip').map(r => r.netbox_id)
    // null = import everything, array = selective
    const rackFilter = selectedRackIds.size === allActionable.length
      ? null   // все выбраны — не ограничиваем
      : selectedRackIds.size > 0
        ? Array.from(selectedRackIds)
        : null  // ни одна не выбрана — импортировать всё (fallback)
    onPreviewReady(preview, selectedDc, selectedSiteId || null, conflictStrategy, rackFilter)
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-1">Предпросмотр импорта</h2>
        <p className="text-sm text-text-secondary">
          Выберите сайт NetBox и ЦОД назначения. Просмотрите что будет импортировано.
        </p>
      </div>

      {/* Config */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
            Сайт NetBox
          </label>
          <select
            value={selectedSiteId}
            onChange={e => setSelectedSiteId(e.target.value ? Number(e.target.value) : '')}
            className="w-full py-2 px-3 bg-bg-input border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-primary"
          >
            <option value="">Все сайты</option>
            {sites.map(s => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.racks_count} стоек)
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
            Целевой ЦОД
          </label>
          <select
            value={selectedDc}
            onChange={e => setSelectedDc(e.target.value)}
            className="w-full py-2 px-3 bg-bg-input border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-primary"
          >
            <option value="">— выберите ЦОД —</option>
            {datacenters.map(dc => (
              <option key={dc.code} value={dc.code}>{dc.name} ({dc.code})</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
            Конфликты
          </label>
          <select
            value={conflictStrategy}
            onChange={e => setConflictStrategy(e.target.value)}
            className="w-full py-2 px-3 bg-bg-input border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-primary"
          >
            <option value="netbox_wins">NetBox приоритет</option>
            <option value="local_wins">Локальные данные</option>
            <option value="manual">Пропускать конфликты</option>
          </select>
        </div>
      </div>

      <button
        onClick={fetchPreview}
        disabled={loading || !selectedDc}
        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-accent-primary hover:bg-accent-primary/90 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-all"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Получаем данные из NetBox...</>
        ) : (
          <><Search className="w-4 h-4" /> Получить предпросмотр</>
        )}
      </button>

      {error && (
        <div className="flex items-start gap-3 p-3 rounded-lg border border-red-500/30 bg-red-500/5">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Preview results */}
      {preview && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-primary">Результаты предпросмотра</span>
            <button onClick={fetchPreview} className="ml-auto text-text-muted hover:text-text-primary">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-2">
            <SummaryCard icon={Layers} label="Стойки" counts={preview.summary.racks} />
            <SummaryCard icon={Server} label="Устройства" counts={preview.summary.devices} />
            <SummaryCard icon={Network} label="Порты" counts={preview.summary.ports} />
            <SummaryCard icon={Cable} label="Кабели" counts={preview.summary.cables} />
            <SummaryCard icon={Users} label="Тенанты" counts={preview.summary.tenants ?? { create: 0, update: 0, skip: 0, conflict: 0, total: 0 }} />
            <SummaryCard icon={CalendarRange} label="Резервации" counts={preview.summary.reservations ?? { create: 0, update: 0, skip: 0, conflict: 0, total: 0 }} />
          </div>

          {/* Selective rack import */}
          {preview.racks.filter(r => r.action !== 'skip').length > 0 && (
            <RackSelector
              racks={preview.racks}
              selected={selectedRackIds}
              onChange={setSelectedRackIds}
            />
          )}

          {/* Warnings */}
          {preview.warnings.length > 0 && (
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium text-yellow-400">
                  {preview.warnings.length} предупреждений
                </span>
              </div>
              <ul className="space-y-1">
                {preview.warnings.slice(0, 5).map((w, i) => (
                  <li key={i} className="text-xs text-text-muted">{w}</li>
                ))}
                {preview.warnings.length > 5 && (
                  <li className="text-xs text-text-muted italic">
                    ...и ещё {preview.warnings.length - 5}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Skipped cables */}
          {preview.skipped_cables.length > 0 && (
            <p className="text-xs text-text-muted">
              ⚠ {preview.skipped_cables.length} кабелей пропущено
              (не Interface-to-Interface соединения)
            </p>
          )}

          {/* Proceed */}
          {hasAnything && (
            <button
              onClick={handleProceed}
              className="w-full py-2.5 px-4 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              {selectedRackIds.size > 0 && selectedRackIds.size < preview.racks.filter(r => r.action !== 'skip').length ? (
                <>Импортировать {selectedRackIds.size} стоек →</>
              ) : (
                <>Продолжить к импорту →</>
              )}
            </button>
          )}

          {!hasAnything && (
            <div className="text-center py-3 text-sm text-text-muted">
              Нет новых объектов для импорта
            </div>
          )}
        </div>
      )}
    </div>
  )
}
